import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, BarChart3, AlertTriangle, Download, 
  FileSpreadsheet, Package, Store, Calendar, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DemandFlowDashboard = () => {
  const [searchParams] = useSearchParams();
  const runId = searchParams.get('run_id');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);
  const { languageConfig } = useLanguage();

  useEffect(() => {
    fetchDashboardData();
  }, [runId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/agent-dashboards/demand-flow`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (dataType, label) => {
    try {
      setExporting(dataType);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/agent-dashboards/demand-flow/export?data_type=${dataType}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${dataType}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${label} exported successfully`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    if (typeof value === 'string' && value.startsWith('₹')) return value;
    const num = parseFloat(value);
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !data?.hasData) {
    return (
      <Card className="m-6">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-lg text-slate-600">{error || 'No DemandFlow analysis found. Please run the agent first.'}</p>
        </CardContent>
      </Card>
    );
  }

  const overview = data?.demandOverview || {};
  const demandForecast = data?.demandForecast || [];
  const categoryDemand = data?.categoryDemand || [];
  const replenishmentPlan = data?.replenishmentPlan || [];

  return (
    <div className="space-y-6 p-6" data-testid="demand-flow-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-blue-600" />
            <TranslatedText text="DemandFlow Dashboard" cacheKey="demandflow:title" />
          </h1>
          <p className="text-slate-500 text-sm mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Demand Intelligence & Buying Safety Analysis" cacheKey="demandflow:subtitle" />
          </p>
        </div>
        <Button onClick={() => handleExport('all_demand', 'All Demand')} disabled={exporting === 'all_demand'}>
          <Download className="h-4 w-4 mr-2" />
          Export Full Analysis
        </Button>
      </div>

      {/* Overview Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <Package className="h-4 w-4" />
              Total SKUs
            </div>
            <p className="text-2xl font-bold text-slate-900">{overview.totalSKUs || 0}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <BarChart3 className="h-4 w-4" />
              Total Revenue
            </div>
            <p className="text-2xl font-bold text-green-600">{overview.totalRevenueFormatted || formatCurrency(overview.totalRevenue)}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <TrendingUp className="h-4 w-4 text-green-500" />
              Core Repeat SKUs
            </div>
            <p className="text-2xl font-bold text-green-600">{overview.coreRepeatSKUs || 0}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              Weak/Dormant SKUs
            </div>
            <p className="text-2xl font-bold text-yellow-600">{overview.weakDormantSKUs || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Demand Forecast Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Demand Forecast
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExport('demand_forecast', 'Demand Forecast')}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {demandForecast.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">SKU ID</th>
                    <th className="px-3 py-2 text-left font-semibold">Name</th>
                    <th className="px-3 py-2 text-left font-semibold">Demand Type</th>
                    <th className="px-3 py-2 text-right font-semibold">Avg Daily</th>
                    <th className="px-3 py-2 text-right font-semibold">Forecasted</th>
                    <th className="px-3 py-2 text-left font-semibold">Confidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {demandForecast.slice(0, 10).map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono">{item.skuId || '-'}</td>
                      <td className="px-3 py-2">{item.skuName || '-'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          item.demandType === 'CORE_REPEAT_DEMAND' ? 'bg-green-100 text-green-700' :
                          item.demandType === 'WEAK_DORMANT' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {item.demandType?.replace(/_/g, ' ') || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">{item.avgDailyDemand?.toFixed(1) || 0}</td>
                      <td className="px-3 py-2 text-right font-medium">{item.forecastedDemand?.toFixed(0) || 0}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          item.confidence === 'HIGH' ? 'bg-green-100 text-green-700' :
                          item.confidence === 'LOW' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {item.confidence || 'MEDIUM'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">No demand forecast data available</p>
          )}
        </CardContent>
      </Card>

      {/* Category Demand */}
      {categoryDemand.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              Demand by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Category</th>
                    <th className="px-3 py-2 text-right font-semibold">Revenue</th>
                    <th className="px-3 py-2 text-right font-semibold">Avg Daily Demand</th>
                    <th className="px-3 py-2 text-left font-semibold">Trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {categoryDemand.slice(0, 10).map((cat, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{cat.category || '-'}</td>
                      <td className="px-3 py-2 text-right">{cat.revenueFormatted || formatCurrency(cat.revenue)}</td>
                      <td className="px-3 py-2 text-right">{cat.avgDailyDemand?.toFixed(1) || 0}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          cat.demandTrend === 'GROWING' ? 'bg-green-100 text-green-700' :
                          cat.demandTrend === 'DECLINING' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {cat.demandTrend || 'STABLE'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Replenishment Plan */}
      {replenishmentPlan.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-green-600" />
                Replenishment Plan
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('replenishment_plan', 'Replenishment Plan')}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-green-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">SKU ID</th>
                    <th className="px-3 py-2 text-left font-semibold">Name</th>
                    <th className="px-3 py-2 text-right font-semibold">Current Stock</th>
                    <th className="px-3 py-2 text-right font-semibold">Suggested Qty</th>
                    <th className="px-3 py-2 text-left font-semibold">Priority</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {replenishmentPlan.slice(0, 10).map((item, idx) => (
                    <tr key={idx} className="hover:bg-green-50">
                      <td className="px-3 py-2 font-mono">{item.skuId || '-'}</td>
                      <td className="px-3 py-2">{item.skuName || '-'}</td>
                      <td className="px-3 py-2 text-right">{item.currentStock || 0}</td>
                      <td className="px-3 py-2 text-right font-bold text-green-600">{item.suggestedQty || 0}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          item.priority === 'HIGH' ? 'bg-red-100 text-red-700' :
                          item.priority === 'LOW' ? 'bg-slate-100 text-slate-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {item.priority || 'MEDIUM'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Plan */}
      {data?.actionPlan && Object.keys(data.actionPlan).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Recommended Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.actionPlan.P0_Immediate?.length > 0 && (
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-semibold text-red-600 mb-2">P0 - Immediate</h4>
                  {data.actionPlan.P0_Immediate.slice(0, 3).map((action, idx) => (
                    <div key={idx} className="text-sm p-2 bg-red-50 rounded mb-2">
                      <p className="font-medium">{action.action}</p>
                    </div>
                  ))}
                </div>
              )}
              {data.actionPlan.P1_ThisWeek?.length > 0 && (
                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold text-orange-600 mb-2">P1 - This Week</h4>
                  {data.actionPlan.P1_ThisWeek.slice(0, 3).map((action, idx) => (
                    <div key={idx} className="text-sm p-2 bg-orange-50 rounded mb-2">
                      <p className="font-medium">{action.action}</p>
                    </div>
                  ))}
                </div>
              )}
              {data.actionPlan.P2_NextCycle?.length > 0 && (
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-blue-600 mb-2">P2 - Next Cycle</h4>
                  {data.actionPlan.P2_NextCycle.slice(0, 3).map((action, idx) => (
                    <div key={idx} className="text-sm p-2 bg-blue-50 rounded mb-2">
                      <p className="font-medium">{action.action}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DemandFlowDashboard;
